"use client";

import { cn } from "@newxsystems/shared";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@templates/design-system";
import { useId, useState, type ReactNode } from "react";
import { resolveKaajPrimitive } from "../../../../client";
import {
  actionClassName,
  getLabel,
  hasDropdownContent,
  hasGroupContent,
  isDropdownItem,
  isGroupItem,
  itemKey,
} from "./MegaMenuBlock.shared";
import type {
  MegaMenuAction,
  MegaMenuGroupListItem,
  MegaMenuItem,
  MegaMenuLink,
} from "./MegaMenuBlock.props";

const Link = resolveKaajPrimitive("Link");

const ChevronIcon = () => (
  <svg
    aria-hidden="true"
    className="size-3.5 shrink-0 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MenuIcon = () => (
  <svg aria-hidden="true" className="size-4" fill="none" viewBox="0 0 20 20">
    <path
      d="M3.5 6h13M3.5 10h13M3.5 14h13"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.8"
    />
  </svg>
);

const MobileActionList = ({ actions }: { actions?: MegaMenuAction[] | null }) => {
  const visibleActions = actions ?? [];

  if (visibleActions.length === 0) return null;

  return (
    <div className="grid gap-3">
      {visibleActions.map((action, index) => (
        <DrawerClose asChild key={itemKey("mobile-action", index, action)}>
          <Link
            aria-label={action.ariaLabel}
            className={cn(actionClassName(action.style), "min-h-12 w-full justify-center")}
            href={action.href}
            rel={action.target === "_blank" ? "noopener noreferrer" : undefined}
            target={action.target ?? undefined}
          >
            {action.text}
          </Link>
        </DrawerClose>
      ))}
    </div>
  );
};

const MobileLink = ({ item }: { item: MegaMenuLink }) => (
  <DrawerClose asChild>
    <Link
      aria-label={item.ariaLabel}
      className="block rounded-2xl px-4 py-3 text-sm font-bold leading-5 text-[#151927] transition-colors duration-200 hover:bg-[#F6F8FF] hover:text-[#2144FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] motion-reduce:transition-none"
      href={item.href}
      rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
      target={item.target ?? undefined}
    >
      <span className="[overflow-wrap:anywhere]">{item.text}</span>
    </Link>
  </DrawerClose>
);

const MobileGroupLinks = ({ group }: { group: MegaMenuGroupListItem }) => {
  const links = group.links ?? [];
  const label = group.header;

  if (links.length === 0) return null;

  return (
    <section className="rounded-2xl border border-[#E6ECFF] bg-[#FAFBFF] p-2">
      {label ? (
        <h3 className="px-3 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#2144FF] [overflow-wrap:anywhere]">
          {label}
        </h3>
      ) : null}
      <div className="grid gap-1">
        {links.map((link, index) => (
          <MobileLink key={itemKey("mobile-group", index, link)} item={link} />
        ))}
      </div>
    </section>
  );
};

const MobileDetails = ({ label, children }: { label: string; children: ReactNode }) => (
  <details className="group rounded-3xl border border-[#D9E1FF] bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)]">
    <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 rounded-3xl px-4 py-3 text-sm font-extrabold text-[#151927] marker:hidden transition-colors duration-200 hover:bg-[#F6F8FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] motion-reduce:transition-none group-open:rounded-b-none [&::-webkit-details-marker]:hidden">
      <span className="[overflow-wrap:anywhere]">{label}</span>
      <ChevronIcon />
    </summary>
    <div className="max-h-[min(52dvh,28rem)] overflow-y-auto overscroll-contain border-t border-[#E6ECFF] [scrollbar-gutter:stable]">
      {children}
    </div>
  </details>
);

const MobileItem = ({ item }: { item: MegaMenuItem }) => {
  const label = getLabel(item);

  if (isDropdownItem(item)) {
    const links = item.links ?? [];
    if (!label || !hasDropdownContent(item)) return null;
    return (
      <MobileDetails label={label}>
        {links.length ? (
          <div className="grid gap-1 px-2 py-3">
            {links.map((link, index) => (
              <MobileLink key={itemKey("mobile-dropdown", index, link)} item={link} />
            ))}
          </div>
        ) : null}
      </MobileDetails>
    );
  }

  if (isGroupItem(item)) {
    if (!label || !hasGroupContent(item)) return null;
    return (
      <MobileDetails label={label}>
        <div className="grid gap-3 px-3 py-3">
          {(item.groups ?? []).map((group, index) => (
            <MobileGroupLinks key={`${group.header}-${index}`} group={group} />
          ))}
        </div>
      </MobileDetails>
    );
  }

  return <MobileLink item={item as MegaMenuLink} />;
};

export const MegaMenuMobileDrawer = ({
  actions,
  items,
  label,
}: {
  actions?: MegaMenuAction[] | null;
  items?: MegaMenuItem[] | null;
  label?: string | null;
}) => {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const visibleItems = items ?? [];
  const hasActions = Boolean(actions?.length);

  if (visibleItems.length === 0 && !hasActions) return null;

  return (
    <Drawer direction="right" open={open} onOpenChange={setOpen} modal>
      <DrawerTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#D9E1FF] bg-white px-4 py-2 text-sm font-extrabold text-[#2144FF] shadow-sm transition-[background-color,border-color,box-shadow] duration-200 hover:border-[#2144FF] hover:bg-[#F6F8FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] motion-reduce:transition-none"
          aria-controls={open ? titleId : undefined}
          aria-expanded={open}
        >
          {label ? <span>{label}</span> : null}
          <MenuIcon />
        </button>
      </DrawerTrigger>
      <DrawerContent
        direction="right"
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        className="h-dvh w-[min(28rem,100vw)] max-w-none border-[#D9E1FF] bg-white p-0 text-[#151927] shadow-[0_28px_80px_rgba(21,25,39,0.24)]"
      >
        <DrawerHeader className="shrink-0 border-b border-[#E6ECFF] px-5 py-5 pr-14 text-left">
          <DrawerTitle
            id={titleId}
            className="text-base font-black tracking-[-0.035em] text-[#151927]"
          >
            Navigation menu
          </DrawerTitle>
          <DrawerDescription id={descriptionId} className="text-sm leading-6 text-[#566179]">
            Browse site sections and request support.
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <div className="grid gap-2">
            {visibleItems.map((item, index) => (
              <MobileItem key={`${getLabel(item) ?? "mobile-item"}-${index}`} item={item} />
            ))}
          </div>
        </div>

        {hasActions ? (
          <DrawerFooter className="sticky bottom-0 shrink-0 border-t border-[#E6ECFF] bg-white/95 px-4 py-4 shadow-[0_-18px_42px_rgba(33,68,255,0.08)] backdrop-blur-xl">
            <MobileActionList actions={actions} />
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
};

MegaMenuMobileDrawer.displayName = "Block::MegaMenuMobileDrawer";
